import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/shared/ui/Input';
import { Select } from '@/shared/ui/Select';
import { Spinner, EmptyState } from '@/shared/ui/Spinner';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { formatDateTime } from '@/lib/utils';
import { useFieldReports } from './field-reports.api';
import { FieldReportDetailDialog } from './FieldReportDetailDialog';
import type { FieldReportStatus } from '@/shared/api/types';

const STATUS_FILTER: { value: FieldReportStatus | ''; label: string }[] = [
  { value: '', label: 'Alle Status' },
  { value: 'SUBMITTED', label: 'Eingegangen' },
  { value: 'TRANSCRIBING', label: 'Transkription' },
  { value: 'REVIEW_REQUIRED', label: 'Prüfung nötig' },
  { value: 'APPROVED', label: 'Geprüft' },
  { value: 'REJECTED', label: 'Abgelehnt' },
];

export function FieldReportsPage() {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<FieldReportStatus | ''>('');
  const [onlyExtra, setOnlyExtra] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const { data, isLoading } = useFieldReports({ q, status, withExtraServices: onlyExtra });

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-slate-900">Meldungen</h1>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-[1fr_auto_auto]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="In Text und Transkript suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select
          value={status}
          onChange={(e) => setStatus(e.target.value as FieldReportStatus | '')}
          className="sm:w-44"
        >
          {STATUS_FILTER.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </Select>
        <label className="flex items-center gap-2 whitespace-nowrap text-sm text-slate-600">
          <input
            type="checkbox"
            checked={onlyExtra}
            onChange={(e) => setOnlyExtra(e.target.checked)}
            className="h-4 w-4 rounded"
          />
          Nur mit Zusatzleistung
        </label>
      </div>

      {isLoading ? (
        <Spinner />
      ) : !data || data.items.length === 0 ? (
        <EmptyState title="Keine Meldungen" hint="Meldungen aus dem Außendienst erscheinen hier." />
      ) : (
        <ul className="space-y-2">
          {data.items.map((r) => (
            <li
              key={r.id}
              onClick={() => setDetailId(r.id)}
              className="cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-3 hover:border-brand-300 hover:shadow-sm"
            >
              <div className="flex items-center justify-between gap-2">
                <p className="truncate font-medium text-slate-900">{r.project.title}</p>
                <StatusBadge status={r.status} />
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-slate-500">
                <span>{r.project.customer.companyName}</span>
                {r.employee && <span>{r.employee.displayName}</span>}
                <span>{formatDateTime(r.submittedAt)}</span>
                {r._count && (
                  <span>
                    📷 {r._count.media} · ⏱ {r._count.workTimes} · ⚠ {r._count.extraServices}
                  </span>
                )}
              </div>
              {r.noteText && <p className="mt-1 truncate text-sm text-slate-600">{r.noteText}</p>}
            </li>
          ))}
        </ul>
      )}

      {data && <p className="mt-3 text-xs text-slate-400">{data.total} Meldungen</p>}

      <FieldReportDetailDialog reportId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
